import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Head from '../components/Head';
import Foot from '../components/Foot';
import './PhotoGallery.css';

const apiUrl = process.env.REACT_APP_API_URL;

// 3 columns on desktop, 2 on tablet, 1 on small phones.
const getColumnCount = () => {
  const w = typeof window === 'undefined' ? 1200 : window.innerWidth;
  if (w >= 992) return 3;
  if (w >= 576) return 2;
  return 1;
};

// Masonry placement that keeps "latest first": photos arrive newest-first and
// each one goes into the currently shortest column (leftmost wins a tie), so
// the newest photos fill the top row left-to-right and the rest flow down
// without gaps. `ratios` maps photo id -> height/width of the real image.
const buildColumns = (photos, ratios, columnCount) => {
  const columns = Array.from({ length: columnCount }, () => ({ items: [], height: 0 }));
  photos.forEach((photo, index) => {
    let target = columns[0];
    for (const col of columns) {
      if (col.height < target.height) target = col;
    }
    const ratio = ratios[photo.id] || 0.75;
    target.items.push({ photo, index, ratio });
    target.height += ratio;
  });
  return columns;
};

function PhotoGallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [ratios, setRatios] = useState(null); // photo id -> height/width, set once measured
  const [columnCount, setColumnCount] = useState(getColumnCount());

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/public/photo-gallery`);
        if (response.data.success) {
          setPhotos(response.data.photos);
        } else {
          setError(response.data.message || 'Failed to load photo gallery.');
        }
      } catch (err) {
        setError('Error fetching photo gallery. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  // Measure every photo's real proportions before laying out, so the columns
  // can be balanced and nothing jumps around while images load.
  useEffect(() => {
    if (photos.length === 0) return undefined;
    let cancelled = false;
    const found = {};
    Promise.all(
      photos.map(
        (photo) =>
          new Promise((resolve) => {
            const img = new Image();
            const done = () => resolve();
            img.onload = () => {
              if (img.naturalWidth > 0) found[photo.id] = img.naturalHeight / img.naturalWidth;
              done();
            };
            img.onerror = done;
            img.src = `${apiUrl}/${photo.image_path}`;
            setTimeout(done, 15000); // never block the gallery on one slow image
          })
      )
    ).then(() => {
      if (!cancelled) setRatios(found);
    });
    return () => {
      cancelled = true;
    };
  }, [photos]);

  useEffect(() => {
    const onResize = () => setColumnCount(getColumnCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const showPrev = () => setPhotoIndex((i) => (i + photos.length - 1) % photos.length);
  const showNext = () => setPhotoIndex((i) => (i + 1) % photos.length);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      else if (e.key === 'ArrowLeft') setPhotoIndex((i) => (i + photos.length - 1) % photos.length);
      else if (e.key === 'ArrowRight') setPhotoIndex((i) => (i + 1) % photos.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, photos.length]);

  return (
    <>
      <Head />
      {/* Start Hero */}
      <section className="common-page-hero">
        <img
          src="assets/img/bg/common-bg.png"
          className="common-hero-bg ak-bg"
          alt="..."
        />
        <div className="common-hero-content">
          <div className="common-hero-info">
            <div className="ak-section-heading ak-style-1 animation-title-content animation-style3" style={{ marginTop: 100 }}>
              <p className="ak-section-subtitle animation-title">Discover</p>
              <h2 className="ak-section-title animation-title">Photo Gallery</h2>
            </div>
            <p className="back-btn">
              <a href="/">Home</a> / Discover / Photo Gallery
            </p>
          </div>
          <a href="" className="phone-number">
            +91 90486 48777
          </a>
        </div>
      </section>
      {/* End Hero */}
      <div className="ak-height-60 ak-height-lg-50" />
      <section>
        <div className="container">
          {loading || (photos.length > 0 && !ratios && !error) ? (
            <div className="text-center">Loading photo gallery...</div>
          ) : error ? (
            <div className="text-center text-danger">{error}</div>
          ) : photos.length === 0 ? (
            <div className="text-center text-warning">No photos found.</div>
          ) : (
            <div className="photo-masonry">
              {buildColumns(photos, ratios, columnCount).map((col, colIndex) => (
                <div className="photo-masonry-col" key={colIndex}>
                  {col.items.map(({ photo, index, ratio }) => (
                    <button
                      type="button"
                      className="photo-masonry-item"
                      key={photo.id}
                      onClick={() => openLightbox(index)}
                      aria-label={photo.title ? `View photo: ${photo.title}` : 'View photo'}
                    >
                      <img
                        src={`${apiUrl}/${photo.image_path}`}
                        alt={photo.title || 'Gallery photo'}
                        style={{ aspectRatio: `1 / ${ratio}` }}
                      />
                      {photo.title && <span className="photo-masonry-caption">{photo.title}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <div className="ak-height-20 ak-height-lg-30" />
      <Foot />

      {isLightboxOpen && photos[photoIndex] && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close"
            style={{
              position: 'absolute', top: 20, right: 24, background: 'transparent', border: 'none',
              color: '#fff', fontSize: 32, lineHeight: 1, cursor: 'pointer', zIndex: 2001,
            }}
          >
            &times;
          </button>

          {photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="Previous"
              style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none', color: '#fff', fontSize: 40,
                lineHeight: 1, cursor: 'pointer', zIndex: 2001,
              }}
            >
              &#8249;
            </button>
          )}

          <img
            src={`${apiUrl}/${photos[photoIndex].image_path}`}
            alt={photos[photoIndex].title || 'Gallery photo'}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }}
          />

          {photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="Next"
              style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none', color: '#fff', fontSize: 40,
                lineHeight: 1, cursor: 'pointer', zIndex: 2001,
              }}
            >
              &#8250;
            </button>
          )}

          {photos[photoIndex].title && (
            <div style={{ color: '#fff', marginTop: 16, fontSize: 16, textAlign: 'center' }}>
              {photos[photoIndex].title}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default PhotoGallery;
